import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { sampleOrbit } from '../lib/orbit';
import { useScale, scaleVec3 } from '../store/scaleStore';
import { usePlanet9Cloud } from '../store/planet9CloudStore';

const N_ORBITS = 200;
const SEGMENTS_PER_ORBIT = 96;

interface Props {
  visible: boolean;
}

/**
 * Nuage d'orbites candidates Planet 9 — ensemble probabiliste, jamais une "position".
 * Les orbites sont rendues comme un seul LineSegments (1 draw call) :
 *   - 200 orbites × 96 segments × 2 endpoints = 38 400 vertices
 *   - opacité par sample dépendant de son poids posterior (heuristique)
 *   - couleur violet/magenta progressive selon le poids
 */
export default function Planet9Cloud({ visible }: Props) {
  const samples = usePlanet9Cloud((s) => s.samples);
  const load = usePlanet9Cloud((s) => s.load);
  const mode = useScale((s) => s.mode);

  useEffect(() => {
    if (visible) void load(N_ORBITS);
  }, [visible, load]);

  const { positions, colors } = useMemo(() => {
    if (!samples.length) return { positions: new Float32Array(0), colors: new Float32Array(0) };

    // Trier par poids décroissant et garder les top N
    const sorted = [...samples].sort((a, b) => b.weight - a.weight).slice(0, N_ORBITS);
    const maxW = sorted[0]?.weight ?? 1;

    // Chaque orbite a SEGMENTS_PER_ORBIT+1 points → SEGMENTS_PER_ORBIT * 2 vertices
    // pour un LineSegments (paires consécutives forment un segment).
    const totalVerts = sorted.length * SEGMENTS_PER_ORBIT * 2;
    const positions = new Float32Array(totalVerts * 3);
    const colors = new Float32Array(totalVerts * 3);

    let v = 0;
    const colA = new THREE.Color('#5e3ddb'); // poids faible (bleu-violet)
    const colB = new THREE.Color('#ff8ad4'); // poids fort (magenta)
    const tmp = new THREE.Color();

    for (const s of sorted) {
      const orbit = sampleOrbit(
        {
          a_au: s.a_au,
          e: s.e,
          i_deg: s.i_deg,
          Omega_deg: s.Omega_deg,
          omega_deg: s.omega_deg,
        },
        SEGMENTS_PER_ORBIT,
      );
      // Couleur lerp selon poids normalisé
      const t = maxW > 0 ? Math.min(1, Math.pow(s.weight / maxW, 0.4)) : 0;
      tmp.copy(colA).lerp(colB, t);

      for (let k = 0; k < SEGMENTS_PER_ORBIT; k++) {
        // Repère écliptique X,Y,Z → scène X, Z, -Y
        const a = scaleVec3([orbit[k][0], orbit[k][2], -orbit[k][1]], mode);
        const b = scaleVec3([orbit[k + 1][0], orbit[k + 1][2], -orbit[k + 1][1]], mode);
        positions[v * 3] = a[0]; positions[v * 3 + 1] = a[1]; positions[v * 3 + 2] = a[2];
        colors[v * 3] = tmp.r; colors[v * 3 + 1] = tmp.g; colors[v * 3 + 2] = tmp.b;
        v++;
        positions[v * 3] = b[0]; positions[v * 3 + 1] = b[1]; positions[v * 3 + 2] = b[2];
        colors[v * 3] = tmp.r; colors[v * 3 + 1] = tmp.g; colors[v * 3 + 2] = tmp.b;
        v++;
      }
    }
    return { positions, colors };
  }, [samples, mode]);

  if (!visible || positions.length === 0) return null;

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));

  return (
    <lineSegments>
      <bufferGeometry attach="geometry" {...geom} />
      <lineBasicMaterial vertexColors transparent opacity={0.16} depthWrite={false} />
    </lineSegments>
  );
}
